import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, useOrgName, useTerms } from '../../layout';
import { attributionText } from './queueText';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Card, Kpi, KpiLedger } from '../../components/shared/Card';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { HBars, LineChart, Sparkline, useTableToggle } from '../../components/shared/charts';
import { analyticsApi, dayLabel, nameList, useAnalytics } from '../../services/analyticsApi';

export { QueueDetailPage } from './QueueDetailPage';

function SlaMeter({ value, target, hasCalls }) {
  const below = hasCalls && value < target;
  return (
    <div className="row" style={{ gap: 8 }}>
      <div className="meter grow">
        <div className="fill" style={{ width: `${hasCalls ? value : 0}%`, background: below ? 'var(--critical-mark)' : 'var(--series-1)' }} />
        <div className="target" style={{ left: `${target}%` }} />
      </div>
      <span className="small num" style={{ width: 48, textAlign: 'right', ...(below ? { color: 'var(--critical)', fontWeight: 600 } : {}) }}>
        {hasCalls ? `${Number(value).toFixed(1)}%` : '—'}
      </span>
    </div>
  );
}

function WaitCard({ data }) {
  const table = useTableToggle();
  const busiest = data.queues.slice(0, 4);
  const days = busiest[0]?.daily.map((d) => d.date) || [];
  const target = data.totals.target.answerWithinSeconds;
  return (
    <Card className="c-7" title="Average wait, busiest queues" description="Seconds from entering the queue to an agent answering" actions={table.button}>
      <LineChart
        labels={days.map(dayLabel)}
        xName="Day"
        height={240}
        yFmt={(v) => fmt.mmss(v)}
        series={busiest.map((q, i) => ({ name: q.queueName, values: q.daily.map((d) => d.averageWaitSeconds), slot: i + 1 }))}
        target={{ value: target, label: `SLA threshold ${fmt.mmss(target)}` }}
        showTable={table.on}
      />
    </Card>
  );
}

export function QueuesPage() {
  const org = useOrgName();
  const h = useHierarchyFilter();
  const terms = useTerms();
  const navigate = useNavigate();
  const { data, error, retry } = useAnalytics(() => analyticsApi.queues(h.params), JSON.stringify(h.params));
  const levelPlural = h.topLevelPlural || 'Divisions';

  const exportColumns = [
    { key: 'queueName', header: 'Queue' },
    { key: 'attributedTo', header: 'Attributed to', exportValue: (q) => attributionText(q.attributedTo, h.topLevelName, levelPlural, terms).name },
    { key: 'totalCalls', header: 'Offered' },
    { key: 'serviceLevelPercent', header: 'SLA met %' },
    { key: 'abandonRate', header: 'Abandoned %' },
    { key: 'averageWaitSeconds', header: 'Avg wait', exportValue: (q) => fmt.mmss(q.averageWaitSeconds) },
    { key: 'agents', header: 'Agents' },
  ];

  let body;
  if (error) body = <ErrorState title="Queues couldn't load" body="Vertolytics couldn't reach the analytics service. Queue figures are unavailable until it responds." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.queues.length)
    body = <EmptyState icon="layers" title="No queue calls in this period" body={`Queues appear once a call is routed to them. If ${org} just connected, the 30-day import fills this view within about an hour.`} />;
  else {
    const t = data.totals;
    const target = Number(t.target.answerTargetPercent);
    const ignore = t.ignoreShortCallsUnderSeconds;
    body = (
      <div className="stack-lg">
        <KpiLedger>
          <Kpi label="Calls offered to queues" value={fmt.int(t.offeredCalls)} meta={`${t.shareOfAllCalls.toFixed(0)}% of all calls`} />
          <Kpi label="SLA met" value={`${t.serviceLevelPercent.toFixed(1)}%`} meta={`target ${target}%`} />
          <Kpi label="Average wait" value={fmt.mmss(t.averageWaitSeconds)} meta={`longest ${fmt.mmss(t.longestWaitSeconds)}`} />
          <Kpi label="Abandon rate" value={`${t.abandonRate.toFixed(1)}%`} meta={`${fmt.int(t.abandonedCalls)} calls`} />
          <Kpi
            label="Queues below SLA"
            value={<span style={t.belowTarget.length ? { color: 'var(--critical)' } : undefined}>{t.belowTarget.length}</span>}
            meta={t.belowTarget.length ? nameList(t.belowTarget) : 'all queues on target'}
          />
        </KpiLedger>
        <div className="grid">
          <Card
            className="c-12"
            flush
            title="All queues"
            description={`${data.queues.length} ${data.queues.length === 1 ? 'queue' : 'queues'}, sorted by calls offered. The black tick on each SLA bar is the ${target}% target.`}
          >
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Queue</th>
                    <th>Attributed to</th>
                    <th className="n">Offered</th>
                    <th style={{ minWidth: 180 }}>SLA met</th>
                    <th className="n">Abandoned</th>
                    <th className="n">Avg wait</th>
                    <th className="n">Agents</th>
                    <th>Calls per day</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queues.map((q) => {
                    const attribution = attributionText(q.attributedTo, h.topLevelName, levelPlural, terms);
                    const open = () => navigate(`/queues/${encodeURIComponent(q.queueId)}`);
                    return (
                      <tr key={q.queueId} className="clickable" tabIndex={0} onClick={open} onKeyDown={(e) => e.key === 'Enter' && open()}>
                        <td>
                          <div className="cell-title">{q.queueName}</div>
                        </td>
                        <td>
                          <div className="small">{attribution.name}</div>
                          <div className="cell-sub">{attribution.how}</div>
                        </td>
                        <td className="n">{fmt.int(q.totalCalls)}</td>
                        <td>
                          <SlaMeter value={q.serviceLevelPercent} target={Number(q.targets.answerTargetPercent)} hasCalls={q.serviceLevelCalls > 0} />
                        </td>
                        <td className="n">{q.abandonRate.toFixed(1)}%</td>
                        <td className="n">{fmt.mmss(q.averageWaitSeconds)}</td>
                        <td className="n">{q.agents}</td>
                        <td>
                          <Sparkline values={q.daily.map((d) => d.calls)} width={90} height={22} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          <WaitCard data={data} />
          <Card className="c-5" title="Abandoned calls by queue" description={`Callers who hung up before an agent answered${ignore ? ` (over ${ignore} ${ignore === 1 ? 'second' : 'seconds'})` : ''}`}>
            <HBars labelWidth="2fr" items={data.queues.slice(0, 8).map((q) => ({ label: q.queueName, value: q.abandonedCalls, suffix: `${q.abandonRate.toFixed(1)}%` }))} />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Page title="Queues" subtitle={`Teams call queues, with the ${h.topLevelName || 'Division'} or ${terms.communityLower} their calls are attributed to.`} exportConfig={{ name: 'Queues', columns: exportColumns, rows: data?.queues || [] }}>
      {body}
    </Page>
  );
}
